import { Link } from "@tanstack/react-router";
import { MoreHorizontal, Wheat, ChefHat, UtensilsCrossed } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Button } from "@/components/ui/button";
import type { DisplayRecipe, RecipeStatus } from "./recipe-adapters";

function StatusBadge({ status }: { status: RecipeStatus }) {
  const { i18n } = useLingui();

  const styles: Record<RecipeStatus, string> = {
    Published: "bg-[var(--tag-blue)] text-(--brand-white)",
    Draft: "bg-(--shell-muted-bg) text-(--color-text-secondary)",
    Archived: "bg-(--color-surface-accent) text-(--color-text-secondary)",
  };

  return (
    <span
      className={`inline-flex h-8 shrink-0 items-center rounded-full px-2 text-sm font-medium ${styles[status]}`}
    >
      {status === "Published"
        ? i18n._(msg`Published`)
        : status === "Draft"
          ? i18n._(msg`Draft`)
          : i18n._(msg`Archived`)}
    </span>
  );
}

function RecipeCardSkeleton() {
  return (
    <div className="flex w-100 shrink-0 flex-col overflow-hidden rounded-xl border border-(--color-border) bg-(--shell-muted-bg) p-1">
      <div className="h-45 w-full animate-pulse rounded-xl bg-(--shell-border)" />
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-2">
          <div className="h-5 w-2/3 animate-pulse rounded bg-(--shell-border)" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-(--shell-border)" />
        </div>
        <div className="h-8 w-28 animate-pulse rounded-full bg-(--shell-border)" />
      </div>
    </div>
  );
}

function RecipeCard({ recipe }: { recipe: DisplayRecipe }) {
  const { i18n } = useLingui();

  const PlaceholderIcon =
    recipe.name.toLowerCase().includes("pasta") || recipe.name.toLowerCase().includes("gnocchi")
      ? UtensilsCrossed
      : ChefHat;

  return (
    <Link
      to="/recipes/$recipeId"
      params={{ recipeId: String(recipe.id) }}
      className="flex w-100 shrink-0 flex-col overflow-hidden rounded-xl border border-(--color-border) bg-(--shell-muted-bg) p-1 transition-colors hover:border-(--shell-border)"
    >
      <div className="relative flex h-45 shrink-0 items-end overflow-hidden rounded-xl p-2">
        {recipe.imageUrl ? (
          <img
            alt={recipe.name}
            className="absolute inset-0 size-full rounded-xl object-cover"
            src={recipe.imageUrl}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-(--shell-border)">
            <PlaceholderIcon className="size-16 text-(--shell-border)" />
          </div>
        )}

        <div className="relative flex h-8 items-center gap-1 rounded-full bg-(--color-surface-accent)/90 px-3 py-2 backdrop-blur-sm">
          <Wheat className="size-4 text-(--color-text-secondary)" />
          <span className="text-sm font-medium text-(--color-text-secondary)">{i18n._(msg`Gluten`)}</span>
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-xl font-medium text-(--color-text-primary)">{recipe.name}</span>
              <StatusBadge status={recipe.status} />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 shrink-0 text-(--color-text-secondary) hover:text-(--color-text-primary)"
              aria-label={i18n._(msg`More options`)}
            >
              <MoreHorizontal className="size-5" />
            </Button>
          </div>
          {(recipe.station || recipe.prepTime) ? (
            <p className="text-sm text-(--color-text-secondary)">
              {[
                recipe.station && `${i18n._(msg`Station`)}: ${recipe.station}`,
                recipe.prepTime && `${i18n._(msg`Prep`)}: ${recipe.prepTime}`,
              ]
                .filter(Boolean)
                .join(" • ")}
            </p>
          ) : null}
        </div>

        {recipe.target ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-(--color-text-secondary)">{i18n._(msg`Targets:`)}</span>
            <div className="flex h-8 items-center rounded-full bg-(--color-surface-accent)/80 px-3 backdrop-blur-sm">
              <span className="text-sm font-medium text-(--color-text-secondary)">{recipe.target}</span>
            </div>
          </div>
        ) : null}
      </div>
    </Link>
  );
}

export function RecipeSection({
  title,
  count,
  recipes,
  isLoading,
}: {
  title: string;
  count: number;
  recipes: DisplayRecipe[];
  isLoading: boolean;
}) {
  const { i18n } = useLingui();

  return (
    <div className="flex w-full shrink-0 flex-col gap-3">
      <div className="flex items-center justify-between px-3">
        <div className="flex items-center gap-3">
          <span className="text-[28px] font-semibold leading-none text-(--color-text-primary)">{title}</span>
          <div className="flex h-8 min-w-11 items-center justify-center rounded-full bg-(--shell-muted-bg) px-3">
            <span className="text-sm font-medium leading-6 text-(--color-text-secondary)">{count}</span>
          </div>
        </div>
        <Button
          variant="link"
          className="h-auto p-0 text-sm font-bold leading-6 text-(--sidebar-footer)"
        >
          {i18n._(msg`View all`)}
        </Button>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1">
        {isLoading
          ? Array.from({ length: 3 }).map((_, index) => <RecipeCardSkeleton key={index} />)
          : recipes.map((recipe) => <RecipeCard key={recipe.id} recipe={recipe} />)}
      </div>
    </div>
  );
}
